import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentLanguage, isLanguageSupported } from '../util/languageUtils';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children, initialLocale = 'en' }) => {
  const [currentLocale, setCurrentLocale] = useState(() => {
    // Get from localStorage or use initial locale
    const savedLocale = getCurrentLanguage();
    return isLanguageSupported(savedLocale) ? savedLocale : initialLocale;
  });

  const [messages, setMessages] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load translation messages for the current locale
  const loadMessages = async (locale) => {
    setIsLoading(true);
    try {
      // Import the translation file dynamically
      const translationModule = await import(`../translations/${locale}.json`);
      setMessages(translationModule.default || translationModule);
    } catch (error) {
      console.warn(`Failed to load translations for locale: ${locale}`, error);
      // Fallback to English if the requested locale fails to load
      if (locale !== 'en') {
        try {
          const fallbackModule = await import('../translations/en.json');
          setMessages(fallbackModule.default || fallbackModule);
        } catch (fallbackError) {
          console.error('Failed to load fallback English translations:', fallbackError);
          setMessages({});
        }
      } else {
        setMessages({});
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Change language
  const changeLanguage = async (newLocale) => {
    if (!isLanguageSupported(newLocale) || newLocale === currentLocale) {
      return false;
    }

    setCurrentLocale(newLocale);
    
    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('preferredLanguage', newLocale);
    }

    // Load new messages
    await loadMessages(newLocale);

    return true;
  };

  // Load initial messages
  useEffect(() => {
    loadMessages(currentLocale);
  }, []);

  const value = {
    currentLocale,
    messages,
    isLoading,
    changeLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 